import * as React from 'react'
import type { Location } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { RxBarChart } from '@react-icons/all-files/rx/RxBarChart'
import { RxBookmark } from '@react-icons/all-files/rx/RxBookmark'
import { RxCopy } from '@react-icons/all-files/rx/RxCopy'
import { RxDownload } from '@react-icons/all-files/rx/RxDownload'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import { RxLightningBolt } from '@react-icons/all-files/rx/RxLightningBolt'
import { RxMixerHorizontal } from '@react-icons/all-files/rx/RxMixerHorizontal'
import { RxPlusCircled } from '@react-icons/all-files/rx/RxPlusCircled'
import { RxShare1 } from '@react-icons/all-files/rx/RxShare1'
import { RxTrash } from '@react-icons/all-files/rx/RxTrash'

import mixpanel from '@disco/api/mixpanel'
import {
  INSTANT_INSTRUMENTAL,
  LYRIC_TRANSCRIPTION,
  TRACK_NESTING,
} from '@disco/constants/FeatureTypes'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import { setCopyMetadata } from '@disco/state-management/actions/copy-metadata-actions'
import { removeLockedTracks } from '@disco/state-management/actions/ServerActions'
import { shiftShortlistItem } from '@disco/state-management/actions/ShortlistActions'
import {
  getSelectedLibrary,
  isDeviceTypePhone,
} from '@disco/state-management/selectors/app'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getGroups } from '@disco/state-management/selectors/groups'
import {
  isInstantInstrumentalVisible,
  makeGetIfLockedTrackBelongsToRegularGroups,
  makeGetTrackRestrictions,
} from '@disco/state-management/selectors/tracks'
import { isAudioSeparationTermsAccepted } from '@disco/state-management/selectors/user'
import { getFileTypeByExt, getNounForExt } from '@disco/utils/file-utils'
import { isChannelShared, itemIsAutoTagged } from '@disco/utils/groups'
import {
  getNestedTracksCount,
  isAggregatedSearchTrack,
} from '@disco/utils/tracks'

import type { ExplorerPane } from 'types/explorer'
import type { ActionRestrictions, Group, GroupId } from 'types/group'
import type { PlaylistId } from 'types/playlist'
import type { Track } from 'types/track'

import { ShortlistContext } from 'contexts/shortlist'
import { useDispatch, useSelector } from 'hooks/use-redux'

import { DropdownMenu, Flex } from 'components/ds'
import Tooltip from 'components/ui/tooltip'

import AddTrackToOption from '../add-to'
import AIToolsMenuOption from '../ai-tools'
import CopyTrackToOption from '../copy-to'
import DownloadTrack from '../download-track'
import ManageTrackOption from '../manage-track'
import RemoveFromShortlist from '../remove-from-shortlist'

type OwnProps = ActionRestrictions & {
  closeMenu: (...args: Array<any>) => any
  track: Track
  sourcePlaylistId?: PlaylistId | null | undefined
  sourceShortlistId?: string
  shortlistShortlistItemIndex?: number
  removeFromShortlist?: boolean
  customEditMetadataUrl?: string | null | undefined
  editMetadataModalOpen?: boolean
  allowSharing?: boolean
  downloadTrack?: boolean
  manageNesting?: boolean
  manageTrackFormats?: boolean
  editMetadata?: boolean
  permissionsAndSharing?: boolean
  addToShortlist?: boolean
  deleteTrack?: boolean
  addToCustomGroup?: boolean
  addToNewCustomGroup?: boolean
  group?: Group | null | undefined
  groupId?: GroupId | null | undefined
  buttonSize?: 'default' | 'compact'
  copyMetadata?: boolean
  location: Location
  pane?: ExplorerPane
  // kept for backward compat with existing callers; no longer used internally
  submenuPosition?: 'left' | 'right'
}

type MixpanelContext =
  | 'Playlist creator'
  | 'Channel'
  | 'Explorer'
  | 'Aggregated search'
  | 'Shared Channels'

function getMixpanelContext({
  group,
  library,
  pane,
  shortlistId,
  track,
}: {
  group?: Group | null
  library: ReturnType<typeof getSelectedLibrary>
  pane?: ExplorerPane
  shortlistId?: string | null
  track: Track
}): MixpanelContext {
  let context: MixpanelContext = 'Explorer'
  if (shortlistId) context = 'Playlist creator'
  if (isChannelShared(group)) context = 'Shared Channels'
  else if (library?.group_type === CUSTOM_CHANNEL && pane?.groupId)
    context = 'Channel'
  if (isAggregatedSearchTrack(track)) context = 'Aggregated search'
  return context
}

export const TrackDropdown = ({
  addToCustomGroup,
  allowSharing,
  closeMenu,
  copyMetadata,
  customEditMetadataUrl,
  deleteTrack,
  downloadTrack,
  editMetadata,
  group,
  groupId,
  location,
  manageNesting,
  manageTrackFormats,
  pane,
  removeFromShortlist,
  shortlistShortlistItemIndex,
  sourcePlaylistId,
  sourceShortlistId,
  track,
}: OwnProps): React.ReactNode => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const shortlistContext = React.useContext(ShortlistContext)

  const getTrackRestrictions = React.useMemo(makeGetTrackRestrictions, [])
  const getIfLockedTrackBelongsToRegularGroups = React.useMemo(
    makeGetIfLockedTrackBelongsToRegularGroups,
    [],
  )

  const groups = useSelector(getGroups)
  const { disableDelete } = useSelector(state =>
    getTrackRestrictions(state, { id: track.id }),
  )
  const displayRemoveFromBrowse = useSelector(state =>
    getIfLockedTrackBelongsToRegularGroups(state, { id: track.id }),
  )
  const isLyricsTranscriptionEnabled = useSelector(state =>
    isFeatureEnabled(state, { featureName: LYRIC_TRANSCRIPTION }),
  )
  const isPhone = useSelector(isDeviceTypePhone)
  const shortlists = useSelector(state => state.shortlists.byId)
  const audioSeparationEnabled = useSelector(state =>
    isFeatureEnabled(state, { featureName: INSTANT_INSTRUMENTAL }),
  )
  const audioSeparationVisible = useSelector(isInstantInstrumentalVisible)
  const audioSeparationTermsAccepted = useSelector(
    isAudioSeparationTermsAccepted,
  )
  const trackNestingEnabled = useSelector(state =>
    isFeatureEnabled(state, { featureName: TRACK_NESTING }),
  )
  const library = useSelector(getSelectedLibrary)

  const isAutoTagged = itemIsAutoTagged(track.groups, groups)
  const disabled = !track.uploaded
  const isSharedChannel = isChannelShared(group)

  const mixpanelContext = React.useRef<MixpanelContext>(
    getMixpanelContext({
      group,
      library,
      pane,
      shortlistId: shortlistContext.shortlistId,
      track,
    }),
  ).current
  const initialTrackId = React.useRef(track.id).current

  React.useEffect(() => {
    mixpanel.sendPureEvent('Open menu', {
      Context: mixpanelContext,
      Menu: 'Track',
      'Track ID': initialTrackId,
    })
  }, [initialTrackId, mixpanelContext])

  const share = (() => {
    if (!allowSharing || !track?.permissions?.share_link) return null
    const sourceLibraryId = track.origin?.source_library?.id || ''
    const sharedChannelContext = isSharedChannel ? 'shared-channel' : ''
    let pathname = `/explorer/tracks/${track.id}/share/`
    if (!track?.permissions?.manage && sourceLibraryId)
      pathname = `/explorer/tracks/${track.id}/share/${sourceLibraryId}`
    else if (sharedChannelContext)
      pathname = `/explorer/tracks/${track.id}/share/${sharedChannelContext}`
    return (
      <DropdownMenu.Item
        disabled={!!(track.virus || disabled)}
        onSelect={() => navigate(pathname)}
      >
        <Flex align="center" gap="2">
          <RxShare1 />
          Share
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const stats = (() => {
    if (!track?.permissions?.manage) return null
    return (
      <DropdownMenu.Item
        onSelect={() => navigate(`/explorer/tracks/${track.id}/stats/`)}
      >
        <Flex align="center" gap="2">
          <RxBarChart />
          Stats
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const editMetadataItem = (() => {
    const { transcode_pending: transcodePending } = track
    if (!editMetadata) return null
    const label = !track?.permissions?.manage
      ? `View ${getNounForExt(track.ext)} information`
      : `Edit ${getNounForExt(track.ext)} information`
    return (
      <Tooltip content={transcodePending ? 'Transcoding...' : null}>
        <DropdownMenu.Item
          disabled={disabled || transcodePending}
          className="hidden-phone"
          onSelect={() =>
            navigate(
              customEditMetadataUrl ||
                `/explorer/tracks/${track.id}/edit-metadata`,
            )
          }
        >
          <Flex align="center" gap="2">
            <RxInfoCircled />
            {label}
          </Flex>
        </DropdownMenu.Item>
      </Tooltip>
    )
  })()

  const trackTags = (() => {
    if (!track?.permissions?.manage) return null
    return (
      <DropdownMenu.Item
        onSelect={() => navigate(`/explorer/tracks/${track.id}/tags`)}
      >
        <Flex align="center" gap="2">
          <RxBookmark />
          Add track tags
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const copyMetadataItem = (() => {
    if (!copyMetadata || !track?.permissions?.manage) return null
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger disabled={disabled}>
          <Flex align="center" gap="2">
            <RxCopy />
            Copy track info
          </Flex>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent>
          <DropdownMenu.Item
            onSelect={() => {
              dispatch(setCopyMetadata({ targetTrackIds: [track.id] }))
              mixpanel.sendPureEvent('Copy track info to track (menu)')
              navigate('/explorer/copy-metadata')
            }}
          >
            To this track
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => {
              dispatch(setCopyMetadata({ sourceTrackId: track.id }))
              mixpanel.sendPureEvent('Copy track info from track (menu)')
              navigate('/explorer/copy-metadata')
            }}
          >
            From this track
          </DropdownMenu.Item>
        </DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    )
  })()

  const addTo = (() => {
    if (!track?.permissions?.manage) return null
    return (
      <AddTrackToOption
        group={group}
        closeMenu={closeMenu}
        track={track}
        sourcePlaylistId={sourcePlaylistId}
        excludeShortlistId={sourceShortlistId}
        sourceShortlistId={sourceShortlistId}
        disabled={disabled}
        addToCustomGroup={addToCustomGroup ?? false}
      />
    )
  })()

  const copyTo = (() => {
    if (track?.permissions?.manage) return null
    return (
      <CopyTrackToOption
        group={group}
        closeMenu={closeMenu}
        track={track}
        sourcePlaylistId={sourcePlaylistId}
        excludeShortlistId={sourceShortlistId}
        sourceShortlistId={sourceShortlistId}
        disabled={disabled}
        addToCustomGroup={addToCustomGroup ?? false}
        channelOwner={group?.business?.id}
      />
    )
  })()

  const manage = (() => {
    const isAudioFile = getFileTypeByExt(track.ext) === AUDIO_FILE
    if (!track?.permissions?.manage) return null
    return (
      <ManageTrackOption
        disabled={disabled}
        manageTrackFormats={manageTrackFormats ?? false}
        trackNestingEnabled={trackNestingEnabled}
        manageNesting={manageNesting ?? false}
        closeMenu={closeMenu}
        track={track}
        isPhone={isPhone}
        isAudioFile={isAudioFile}
      />
    )
  })()

  const aiTools = (() => {
    const isAudioFile = getFileTypeByExt(track.ext) === AUDIO_FILE
    if (
      !track?.permissions?.manage ||
      !isAudioFile ||
      (!audioSeparationVisible &&
        !audioSeparationTermsAccepted &&
        !audioSeparationEnabled &&
        !isLyricsTranscriptionEnabled)
    )
      return null
    return (
      <AIToolsMenuOption
        track={track}
        isAutoTagged={isAutoTagged}
        audioSeparationEnabled={audioSeparationEnabled}
        audioSeparationTermsAccepted={audioSeparationTermsAccepted}
        location={location}
        closeMenu={closeMenu}
        mixpanelContext={mixpanelContext}
        mixpanel={mixpanel}
        audioSeparationVisible={audioSeparationVisible}
        isLyricsTranscriptionEnabled={isLyricsTranscriptionEnabled}
      />
    )
  })()

  const downloadTrackItem = (() => {
    if (!downloadTrack) return null
    return (
      <DownloadTrack
        track={track}
        groupId={groupId}
        sourcePlaylistId={sourcePlaylistId}
        onClick={closeMenu}
        isInSharedChannel={isSharedChannel}
        ownerId={group?.business?.id}
        role={group?.business_role}
      />
    )
  })()

  const downloadAllItem = (() => {
    if (
      !downloadTrack ||
      !trackNestingEnabled ||
      !track.nesting_zip_urls ||
      getNestedTracksCount(track) === 0
    )
      return null
    return (
      <DownloadTrack
        track={track}
        groupId={groupId}
        sourcePlaylistId={sourcePlaylistId}
        onClick={closeMenu}
        type="all-versions"
        menuTitle="Download all"
        isInSharedChannel={isSharedChannel}
      />
    )
  })()

  const moveUp = (() => {
    if (!isPhone || !shortlistContext.shortlistId) return null
    const shortlist = shortlists[shortlistContext.shortlistId]
    if (!shortlist) return null
    const position = shortlist.items.findIndex(
      ({ track: t }) => track.id === t.id,
    )
    const isDisabled =
      position + -1 < 0 &&
      !shortlist.sections.find(s => s.position === position)
    return (
      <DropdownMenu.Item
        disabled={isDisabled}
        onSelect={() => {
          if (shortlistContext.shortlistId)
            dispatch(
              shiftShortlistItem(shortlistContext.shortlistId, position, -1),
            )
        }}
      >
        Move track up
      </DropdownMenu.Item>
    )
  })()

  const moveDown = (() => {
    if (!isPhone || !shortlistContext.shortlistId) return null
    const shortlist = shortlists[shortlistContext.shortlistId]
    if (!shortlist) return null
    const position = shortlist.items.findIndex(
      ({ track: t }) => track.id === t.id,
    )
    const isDisabled =
      position + 1 === shortlist.items.length &&
      !shortlist.sections.find(s => s.position === position + 1)
    return (
      <DropdownMenu.Item
        disabled={isDisabled}
        onSelect={() => {
          if (shortlistContext.shortlistId)
            dispatch(
              shiftShortlistItem(shortlistContext.shortlistId, position, 1),
            )
        }}
      >
        Move track down
      </DropdownMenu.Item>
    )
  })()

  const removeFromShortlistItem = (() => {
    if (!removeFromShortlist || !sourceShortlistId || !isPhone) return null
    return (
      <RemoveFromShortlist
        shortlistId={sourceShortlistId}
        // NaN mirrors Number(undefined), so a missing index stays a no-op in removeSelectedItems
        itemIndex={shortlistShortlistItemIndex ?? NaN}
      />
    )
  })()

  const removeFromBrowse = (() => {
    if (!displayRemoveFromBrowse) return null
    return (
      <DropdownMenu.Item
        color="red"
        onSelect={() => {
          dispatch(removeLockedTracks([track]))
          closeMenu()
        }}
      >
        Remove from Browse
      </DropdownMenu.Item>
    )
  })()

  const deleteTrackItem = (() => {
    if (!deleteTrack || !track?.permissions?.delete) return null
    return (
      <DropdownMenu.Item
        color="red"
        disabled={disableDelete}
        onSelect={() => navigate(`/explorer/tracks/${track.id}/delete`)}
      >
        <Flex align="center" gap="2">
          <RxTrash />
          Delete {getNounForExt(track.ext)}
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const editItems = [editMetadataItem, trackTags, copyMetadataItem].filter(
    Boolean,
  )
  const actionItems = [
    addTo,
    copyTo,
    manage,
    aiTools,
    downloadTrackItem,
    downloadAllItem,
  ].filter(Boolean)
  const shortlistItems = [moveUp, moveDown, removeFromShortlistItem].filter(
    Boolean,
  )
  const dangerItems = [removeFromBrowse, deleteTrackItem].filter(Boolean)

  return (
    <>
      {share}
      {stats}

      {editItems.length > 0 && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Label>Edit</DropdownMenu.Label>
          {editItems}
        </>
      )}

      {actionItems.length > 0 && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Label>Actions</DropdownMenu.Label>
          {actionItems}
        </>
      )}

      {shortlistItems.length > 0 && (
        <>
          <DropdownMenu.Separator />
          {shortlistItems}
        </>
      )}

      {dangerItems.length > 0 && (
        <>
          <DropdownMenu.Separator />
          {dangerItems}
        </>
      )}
    </>
  )
}

export default TrackDropdown
